type Range = {
  min: number;
  max?: number;
};

export type CollectionFabric =
  | 'soft-silk'
  | 'nylon-soft-silk'
  | 'silk-cotton'
  | 'cotton'
  | 'linen-cotton'
  | 'tissue-silk';
export type CollectionZari = 'gold' | 'silver' | 'copper' | 'antique';

export type Collection = {
  slug: string;
  name: string;
  synonyms?: string[];
  heroImage: string;
  summary: string;
  description: string;
  priceBandINR: [number, number];
  fabrics: CollectionFabric[];
  patterns: string[];
  borderTypes: string[];
  zari: CollectionZari[];
  occasions: string[];
  highlights: string[];
  quickFacts: Array<{ label: string; value: string }>;
  vibe: 'heritage' | 'festive' | 'contemporary' | 'bridal' | 'minimal';
  tags: string[];
};

export type CollectionFilterOption = {
  value: string;
  label: string;
  description?: string;
  range?: Range;
};

export type CollectionFilterGroup = {
  slug: 'fabric' | 'pattern' | 'border' | 'zari' | 'occasion' | 'price';
  label: string;
  options: CollectionFilterOption[];
};

export const COLLECTIONS: Collection[] = [
  {
    slug: 'elampillai-soft-silk',
    name: 'Elampillai Soft Silk',
    synonyms: ['Soft Silk', 'Arani Soft Silk'],
    heroImage: '/assets/optimized/collections/elampillai-soft-silk.webp',
    summary: 'Feather-soft drape with contrast zari borders for everyday celebrations.',
    description:
      'Merchandised straight from Elampillai looms, this soft silk edit lands in the sweet spot between daily elegance and festive sparkle. Lightweight bodies keep the saree easy to pleat while contrast pallus deliver classic drama.',
    priceBandINR: [849, 1299],
    fabrics: ['soft-silk'],
    patterns: ['butta', 'checks', 'stripes', 'vaira-oosi'],
    borderTypes: ['contrast', 'borderless', 'temple', 'kottanchi', 'korvai'],
    zari: ['gold', 'silver', 'copper'],
    occasions: ['festive', 'wedding', 'office'],
    highlights: ['Grand pallus with soft fall', '5.3 m saree plus 0.9 m blouse sets', 'Silk Mark ready partners'],
    quickFacts: [
      { label: 'Weave', value: '110 Karishma cone finish' },
      { label: 'Feel', value: 'Lightweight, fluid' },
      { label: 'Care', value: 'Dry clean for longest sheen' },
    ],
    vibe: 'heritage',
    tags: ['handloom-inspired', 'soft-finish', 'budget-festive'],
  },
  {
    slug: 'nylon-soft-silk',
    name: 'Nylon Soft Silk',
    heroImage: '/assets/optimized/collections/elampillai-nylon-soft-silk.webp',
    summary: 'Festival-friendly shine with easy maintenance pricing.',
    description:
      'Vibrant colour blocks, neon borders, and quick-wash care make nylon soft silk the go-to bundle for festive gifting drives and society events. The body carries a glassy sheen without the weight of pure silk.',
    priceBandINR: [649, 899],
    fabrics: ['nylon-soft-silk'],
    patterns: ['butta', 'checks', 'stripes'],
    borderTypes: ['contrast', 'borderless'],
    zari: ['gold', 'silver'],
    occasions: ['festive', 'dailywear'],
    highlights: ['Budget-friendly shimmer', 'Quick-wash friendly body', 'Bright neon border stories'],
    quickFacts: [
      { label: 'Length', value: '5.3 m saree set' },
      { label: 'Care', value: 'Gentle hand wash acceptable' },
      { label: 'Perfect for', value: 'Festive gifting packs' },
    ],
    vibe: 'festive',
    tags: ['budget-picks', 'festival-offers'],
  },
  {
    slug: 'silk-cotton',
    name: 'Silk Cotton',
    heroImage: '/assets/optimized/collections/elampillai-silk-cotton.webp',
    summary: 'Airy matte texture with zari-edged sophistication.',
    description:
      'Elampillai silk cotton balances mercerised cotton comfort with silk highlights across borders and pallus. Perfect for workwear upgrades and nine-to-nine dressing with breathable ease.',
    priceBandINR: [660, 1449],
    fabrics: ['silk-cotton'],
    patterns: ['butta', 'brocade', 'stripes'],
    borderTypes: ['contrast', 'temple'],
    zari: ['gold', 'silver'],
    occasions: ['office', 'festive', 'dailywear'],
    highlights: ['All-day breathable base', 'Matte body with zari accents', 'Mix-and-match friendly palette'],
    quickFacts: [
      { label: 'Feel', value: 'Soft matte handfeel' },
      { label: 'Workday ready', value: 'Formal plus festive fittings' },
      { label: 'Care', value: 'Gentle dry clean or steam press' },
    ],
    vibe: 'contemporary',
    tags: ['value', 'workwear'],
  },
  {
    slug: 'kalyani-cotton',
    name: 'Kalyani Cotton',
    heroImage: '/assets/optimized/collections/elampillai-kalyani-cotton.webp',
    summary: 'Dailywear hero with crisp contrast borders.',
    description:
      'The go-to everyday saree across Salem, Kalyani cotton carries breezy handloom comfort with simple stripes and checks. Durable dyeing keeps colours fresh even after weekly rotation.',
    priceBandINR: [560, 900],
    fabrics: ['cotton'],
    patterns: ['checks', 'plain', 'printed'],
    borderTypes: ['contrast'],
    zari: ['gold'],
    occasions: ['dailywear', 'office'],
    highlights: ['Breathable cotton body', 'Contrast borders with pop', 'Built for repeat wear'],
    quickFacts: [
      { label: 'Handloom count', value: 'Approx 80 x 80' },
      { label: 'Care', value: 'Regular mild wash' },
      { label: 'Perfect for', value: 'Uniform programs' },
    ],
    vibe: 'minimal',
    tags: ['budget-daily', 'summer-friendly'],
  },
  {
    slug: 'kubera-pattu',
    name: 'Kubera Pattu (Bridal)',
    heroImage: '/assets/optimized/collections/elampillai-kubera-pattu.webp',
    summary: 'High drama jacquard pallus for stage-ready bridal looks.',
    description:
      'Elampillai bridal ateliers craft Kubera Pattu with rich jacquard pallus, antique toned zari, and blouse lengths that carry story-packed motifs. Designed for spotlight-ready weddings and receptions.',
    priceBandINR: [1500, 3200],
    fabrics: ['soft-silk'],
    patterns: ['brocade', 'butta'],
    borderTypes: ['contrast', 'temple'],
    zari: ['gold', 'antique'],
    occasions: ['wedding', 'festive'],
    highlights: ['Antique gold zari motifs', 'Jacquard blouses bundled', 'Grand pallus built for stage'],
    quickFacts: [
      { label: 'Feel', value: 'Structured, regal' },
      { label: 'Styling', value: 'Pair with temple jewels' },
      { label: 'Care', value: 'Professional polish only' },
    ],
    vibe: 'bridal',
    tags: ['bridal', 'grand-pallu'],
  },
  {
    slug: 'linen-cotton',
    name: 'Linen Cotton',
    heroImage: '/assets/optimized/collections/elampillai-linen-cotton.webp',
    summary: 'Pastel-forward drapes for modern minimal wardrobes.',
    description:
      'Linen cotton edits introduce muted tones, cane-friendly textures, and soft zari hints. A favourite with boutique buyers building a relaxed resort-to-brunch rack.',
    priceBandINR: [950, 1600],
    fabrics: ['linen-cotton'],
    patterns: ['plain', 'stripes'],
    borderTypes: ['contrast', 'borderless'],
    zari: ['gold', 'silver'],
    occasions: ['office', 'dailywear'],
    highlights: ['Airy linen blend feel', 'Soft metallic edges', 'Pastel led palette'],
    quickFacts: [
      { label: 'Care', value: 'Steam press recommended' },
      { label: 'Perfect for', value: 'Boutique capsules' },
      { label: 'Styling', value: 'Minimal jewellery' },
    ],
    vibe: 'minimal',
    tags: ['minimal', 'summer'],
  },
  {
    slug: 'kerala-cotton-tissue',
    name: 'Kerala Cotton / Tissue',
    heroImage: '/assets/optimized/collections/elampillai-kerala-cotton-tissue.webp',
    summary: 'Kasavu inspired ivory and gold signatures.',
    description:
      'From Onam-ready kasavu sarees to tissue silk accents, this capsule focuses on gleaming gold borders, banana leaf motifs, and temple-ready silhouettes.',
    priceBandINR: [1200, 2100],
    fabrics: ['cotton', 'tissue-silk'],
    patterns: ['plain', 'zari-edged'],
    borderTypes: ['contrast', 'temple'],
    zari: ['gold'],
    occasions: ['festive', 'traditional'],
    highlights: ['Signature kasavu palette', 'Temple ready styling', 'Gold zari highlights'],
    quickFacts: [
      { label: 'Feel', value: 'Crisp yet fluid' },
      { label: 'Perfect for', value: 'Festivals and gifting' },
      { label: 'Care', value: 'Dry clean and flat store' },
    ],
    vibe: 'heritage',
    tags: ['south-classic'],
  },
];

export const COLLECTION_FILTER_GROUPS: CollectionFilterGroup[] = [
  {
    slug: 'fabric',
    label: 'Fabric',
    options: [
      { value: 'all', label: 'All fabrics' },
      { value: 'soft-silk', label: 'Soft Silk' },
      { value: 'nylon-soft-silk', label: 'Nylon Soft Silk' },
      { value: 'silk-cotton', label: 'Silk Cotton' },
      { value: 'cotton', label: 'Cotton' },
      { value: 'linen-cotton', label: 'Linen Cotton' },
      { value: 'tissue-silk', label: 'Tissue Silk' },
    ],
  },
  {
    slug: 'pattern',
    label: 'Pattern',
    options: [
      { value: 'all', label: 'All patterns' },
      { value: 'butta', label: 'Butta' },
      { value: 'checks', label: 'Checks' },
      { value: 'stripes', label: 'Stripes' },
      { value: 'brocade', label: 'Brocade' },
      { value: 'vaira-oosi', label: 'Vaira Oosi' },
      { value: 'plain', label: 'Plain' },
      { value: 'printed', label: 'Printed' },
      { value: 'zari-edged', label: 'Zari Edged' },
    ],
  },
  {
    slug: 'border',
    label: 'Border',
    options: [
      { value: 'all', label: 'All borders' },
      { value: 'contrast', label: 'Contrast' },
      { value: 'borderless', label: 'Borderless' },
      { value: 'temple', label: 'Temple' },
      { value: 'kottanchi', label: 'Kottanchi' },
      { value: 'korvai', label: 'Korvai' },
    ],
  },
  {
    slug: 'zari',
    label: 'Zari',
    options: [
      { value: 'all', label: 'All zari' },
      { value: 'gold', label: 'Gold' },
      { value: 'silver', label: 'Silver' },
      { value: 'copper', label: 'Copper' },
      { value: 'antique', label: 'Antique' },
    ],
  },
  {
    slug: 'occasion',
    label: 'Occasion',
    options: [
      { value: 'all', label: 'All occasions' },
      { value: 'wedding', label: 'Wedding' },
      { value: 'festive', label: 'Festive' },
      { value: 'office', label: 'Office' },
      { value: 'dailywear', label: 'Dailywear' },
      { value: 'traditional', label: 'Traditional' },
    ],
  },
  {
    slug: 'price',
    label: 'Price Band (INR)',
    options: [
      { value: 'all', label: 'All price bands' },
      { value: '500-900', label: '500 - 900', range: { min: 500, max: 900 } },
      { value: '900-1500', label: '900 - 1500', range: { min: 900, max: 1500 } },
      { value: '1500-3000', label: '1500 - 3000', range: { min: 1500, max: 3000 } },
      { value: '3000+', label: '3000 and above', range: { min: 3000 } },
    ],
  },
];

export const COLLECTIONS_PAGE_CONTENT = {
  hero: {
    eyebrow: 'Collections',
    title: 'Elampillai Sarees, Curated For Every Celebration',
    subtitle: 'Crafted in Salem, styled for contemporary wardrobes.',
    description:
      'We work directly with Elampillai weavers and wholesale clusters to build a boutique-ready rack: soft silks, Kalyani cottons, Kubera bridal drapes, and linen blends with modern palettes.',
    image: '/assets/optimized/collections/elampillai-boutique-hero.webp',
    highlights: ['Weekly loom-fresh arrivals', 'Silk Mark ready partners', 'Curated by in-house stylists'],
    stats: [
      { label: 'Core weaves', value: '7', helper: 'Soft silk to kasavu tissue' },
      { label: 'Retail ready', value: 'INR 560 - 3200', helper: 'On-shelf price band' },
      { label: 'Lead time', value: '5-7 days', helper: 'Pan-India dispatch' },
    ],
    ctas: [
      { label: 'Talk to a stylist', href: '/contact', primary: true },
      { label: 'Browse filters', href: '#collections-filters' },
    ],
  },
  stories: [
    {
      title: 'Grounded in Elampillai research',
      description:
        'Every capsule is benchmarked against active listings across Elampillai storefronts, so your shelves mirror what discerning shoppers are already searching for.',
      bullets: ['Verified price ladders', 'Real weave terminology', 'Regional trend mapping'],
    },
    {
      title: 'Designed for modular merchandising',
      description:
        'Structured data keeps filters, landing copy, and SKU onboarding in sync. Build campaigns faster with consistent tags and quick facts.',
      bullets: ['Single source of truth data file', 'Ready-to-use filters', 'Scales with CMS onboarding'],
    },
    {
      title: 'Stylists on standby',
      description:
        'Need lookbooks, mannequin styling, or social copy? Our merch team supports launches with rapid turnarounds tuned to your channel.',
      bullets: ['Lookbook requests under 48h', 'Palette matchmaking', 'Shop-floor training aids'],
    },
  ],
  valueProps: {
    title: 'Why boutiques pick the Sree Silks edit',
    points: [
      {
        title: 'Direct weave collaborations',
        description: 'Multi-generation weavers reserve looms for our briefs, keeping motifs and colourways fresh.',
      },
      {
        title: 'QC beyond colour cards',
        description: 'Each batch is tested for colour bleed, zari finish, and length consistency before dispatch.',
      },
      {
        title: 'Merch kits ready',
        description:
          'Product copy, social snippets, and POS prompts ship with your order so teams can launch instantly.',
      },
    ],
    cta: { label: 'Explore our journey', href: '/journey' },
  },
} as const;

export type CollectionsHeroContent = (typeof COLLECTIONS_PAGE_CONTENT)['hero'];
export type CollectionsStory = (typeof COLLECTIONS_PAGE_CONTENT)['stories'][number];
export type CollectionsValueProp = (typeof COLLECTIONS_PAGE_CONTENT)['valueProps']['points'][number];
